import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { trackEvent } from "@/shared/lib/posthog";

import { PENDING_MOBILE_HANDOFF_KEY } from "@/shared/lib/mobile-handoff";
import { Spinner } from "@/components/ui/spinner";

const TOKEN_STORAGE_KEY = "ubik_session_token";
// Re-exported for the callers that import it from here (App.tsx).
export { PENDING_MOBILE_HANDOFF_KEY };

/**
 * /auth/mobile — the Android app's login handoff, modelled on DesktopAuth.
 *
 * Google blocks OAuth inside embedded WebViews (disallowed_useragent), so the
 * app opens THIS page in a Chrome Custom Tab. The normal web login runs there
 * (same origin, no new Google redirect URIs), and once a session exists this
 * page hands the token back to the app through its custom URL scheme. Android
 * resolves ubikcapture:// to the app's intent filter; the app stores the token
 * and closes the tab.
 *
 * No loopback leg (that is a desktop-localhost trick); the deep link IS the
 * transport here.
 */
export default function AuthMobile() {
  const { t } = useTranslation("pages");
  const { authData, loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"preparing" | "sent" | "no-token">("preparing");

  useEffect(() => {
    if (loading) return;

    if (!authData) {
      // Same trick as the desktop handoff: stash a flag, run the ordinary
      // login, and AuthenticatedApp routes back here once the session exists.
      sessionStorage.setItem(PENDING_MOBILE_HANDOFF_KEY, "1");
      // Keep ?from=mobile so the login page shows the organisation-only copy.
      navigate("/login?from=mobile", { replace: true });
      return;
    }

    let token: string | null = null;
    try {
      token = localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      token = null;
    }
    if (!token) {
      setStatus("no-token");
      return;
    }

    try {
      window.location.href = `ubikcapture://auth?token=${encodeURIComponent(token)}`;
    } catch {
      /* scheme not registered — the hint below is all we can offer */
    }
    setStatus("sent");
    trackEvent("mobile_auth_completed", {});
  }, [authData, loading, navigate]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background p-6 text-center text-foreground">
      {status === "preparing" && <Spinner className="size-8 text-muted-foreground" />}
      {status === "sent" && (
        <>
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          <p className="text-base font-semibold">
            {t("auth_mobile.sent", "Signed in — sending you back to the app")}
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {t(
              "auth_mobile.sent_body",
              "If nothing happens, close this tab and reopen the Ubik app.",
            )}
          </p>
        </>
      )}
      {status === "no-token" && (
        <p className="max-w-sm text-sm text-muted-foreground">
          {t(
            "auth_mobile.no_token",
            "Signed in, but no session token was found in this browser. Close this tab and sign in from the app again.",
          )}
        </p>
      )}
    </div>
  );
}
